import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Comment, CreateCommentData } from '../../types/comment';
import MarkdownRenderer from '../common/MarkdownRenderer/MarkdownRenderer';
import CommentDialog from './CommentDialog';
import { logComponentVersion } from '../../utils/version';
import './CommentableMarkdownContent.css';

interface CommentableMarkdownContentProps {
  content: string;
  sectionId: string;
  comments: Comment[];
  onAddComment: (commentData: CreateCommentData) => void;
  onCommentClick: (comment: Comment) => void;
  selectedCommentId?: string | null;
}

const CommentableMarkdownContent: React.FC<CommentableMarkdownContentProps> = ({
  content,
  sectionId,
  comments,
  onAddComment,
  selectedCommentId,
}) => {
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string>('');
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [selectionState, setSelectionState] = useState<'idle' | 'selecting' | 'selected'>('idle');
  const contentRef = useRef<HTMLDivElement>(null);
  const eventListenersRef = useRef<Map<HTMLElement, () => void>>(new Map());
  const rebindTimeoutRef = useRef<number | null>(null);
  const bindEpochRef = useRef<number>(0);
  
  // Use refs to access current state values in event handlers
  const selectedBlockIdRef = useRef<string | null>(null);
  const showCommentDialogRef = useRef<boolean>(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    selectedBlockIdRef.current = selectedBlockId;
  }, [selectedBlockId]);
  
  useEffect(() => {
    showCommentDialogRef.current = showCommentDialog;
  }, [showCommentDialog]);

  // Log component initialization
  useEffect(() => {
    logComponentVersion('💬 CommentableMarkdownContent', `Initializing for sectionId: ${sectionId}`);
  }, [sectionId]);

  // console.log('💬 CommentableMarkdownContent: Rendering for sectionId:', sectionId, 'showDialog:', showCommentDialog);

  // Calculate comment counts per paragraph/block
  const commentCounts = useMemo(() => {
    const counts = new Map<string, number>();
    comments.forEach(comment => {
      const currentCount = counts.get(comment.blockId) || 0;
      counts.set(comment.blockId, currentCount + 1);
    });
    return counts;
  }, [comments]);

  // Clean up event listeners
  const cleanupEventListeners = useCallback(() => {
    // console.log('💬 CommentableMarkdownContent: Cleaning up event listeners');
    eventListenersRef.current.forEach((cleanup) => {
      cleanup();
    });
    eventListenersRef.current.clear();
  }, []);

  // Handle element click for comment creation
  const handleElementClick = useCallback((blockId: string, element: HTMLElement) => {
    return (e: Event) => {
      // console.log('💬 CommentableMarkdownContent: Element clicked for blockId:', blockId);
      e.stopPropagation();
      
      // Diagnostic logging to track guard state
      // console.log('💬 CommentableMarkdownContent: Guard check', {
      //   state_selectedBlockId: selectedBlockId,
      //   ref_selectedBlockId: selectedBlockIdRef.current,
      //   state_dialog: showCommentDialog,
      //   ref_dialog: showCommentDialogRef.current
      // });
      
      // Use refs to check current state values (not stale closures)
      if (selectedBlockIdRef.current !== null || showCommentDialogRef.current) {
        // console.log('💬 CommentableMarkdownContent: Ignoring click - already processing', {
        //   selectedBlockId: selectedBlockIdRef.current,
        //   showDialog: showCommentDialogRef.current
        // });
        return;
      }
      
      // console.log('💬 CommentableMarkdownContent: Processing click for blockId:', blockId);
      setSelectionState('selecting');
      setSelectedBlockId(blockId);
      setSelectedText(element.textContent || '');
      setShowCommentDialog(true);
      setSelectionState('selected');
    };
  }, []); // No dependencies - uses refs for current values

  // Stable reference for content processing - only changes when content or sectionId changes
  const processedContent = useMemo(() => {
    return { content, sectionId, commentCounts };
  }, [content, sectionId, commentCounts]);

  // Add commentable features to DOM elements
  const addCommentableFeatures = useCallback(() => {
    if (!contentRef.current) return;

    bindEpochRef.current += 1;

    const elements = contentRef.current.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre');
    
    // Filter out elements that are nested inside other commentable elements
    const topLevelElements = Array.from(elements).filter(element => {
      // Check if this element is nested inside another commentable element
      const parent = element.parentElement;
      const commentableSelectors = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE'];
      
      // Walk up the DOM tree to see if we're inside another commentable element
      let currentParent = parent;
      while (currentParent && currentParent !== contentRef.current) {
        if (commentableSelectors.includes(currentParent.tagName)) {
          return false; // This element is nested, exclude it
        }
        currentParent = currentParent.parentElement;
      }
      return true; // This is a top-level commentable element
    });
    
    // console.log('💬 CommentableMarkdownContent: Bind epoch', bindEpoch, 'found', topLevelElements.length, 'commentable elements');
    
    topLevelElements.forEach((element, index) => {
      const blockId = `${processedContent.sectionId}-block-${index}`;
      const commentCount = processedContent.commentCounts.get(blockId) || 0;
      
      // Add data attribute for identification
      element.setAttribute('data-block-id', blockId);
      
      // Use CSS classes instead of inline styles to prevent re-renders
      const htmlElement = element as HTMLElement;
      htmlElement.className = `commentable-element ${commentCount > 0 ? 'commentable-element--has-comments' : ''}`;
      
      // Remove any existing comment badges
      const existingBadge = htmlElement.querySelector('.comment-badge');
      if (existingBadge) {
        existingBadge.remove();
      }
      
      // Add comment count badge if there are comments
      if (commentCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'comment-badge';
        badge.textContent = commentCount.toString();
        htmlElement.appendChild(badge);
      }
      
      // Create click handler using the callback
      const clickHandler = handleElementClick(blockId, htmlElement);
      
      // Add event listeners (no hover style changes to prevent re-renders)
      htmlElement.addEventListener('click', clickHandler);
      
      // Store cleanup function
      const cleanup = () => {
        htmlElement.removeEventListener('click', clickHandler);
      };
      eventListenersRef.current.set(htmlElement, cleanup);
      
      // Add title for accessibility
      htmlElement.title = commentCount > 0 
        ? `${commentCount} comment${commentCount > 1 ? 's' : ''} - Click to add more`
        : 'Click to add comment';
    });
  }, [processedContent, handleElementClick]);

  useEffect(() => {
    if (!contentRef.current) return;

    // console.log('💬 CommentableMarkdownContent: Setting up commentable features', {
    //   selectedCommentId,
    //   sectionId: processedContent.sectionId,
    //   commentsCount: comments.length,
    //   showCommentDialog
    // });

    // Always clean up first
    cleanupEventListeners();
    
    // Clear any pending rebind timeout
    if (rebindTimeoutRef.current) {
      clearTimeout(rebindTimeoutRef.current);
      rebindTimeoutRef.current = null;
    }

    // If dialog is open, skip binding to avoid intercepting modal clicks
    if (showCommentDialog) {
      // console.log('💬 CommentableMarkdownContent: Skipping bind while dialog open');
      return;
    }

    // Defer to next frame so DOM is stable after dialog close
    const raf = requestAnimationFrame(() => {
      // console.log('💬 CommentableMarkdownContent: Binding after dialog closed');
      addCommentableFeatures();
      
      // Set up MutationObserver to auto-rebind on DOM changes
      const observer = new MutationObserver(() => {
        if (showCommentDialog) return; // don't rebind while open
        if (!contentRef.current) return;
        
        // Debounce to avoid thrash
        if (rebindTimeoutRef.current) {
          clearTimeout(rebindTimeoutRef.current);
        }
        rebindTimeoutRef.current = window.setTimeout(() => {
          // console.log('💬 CommentableMarkdownContent: DOM changed, re-binding listeners');
          cleanupEventListeners();
          addCommentableFeatures();
        }, 0);
      });

      if (contentRef.current) {
        observer.observe(contentRef.current, { childList: true, subtree: true });
      }

      // Store observer for cleanup
      return observer;
    });

    // Cleanup function
    return () => {
      // console.log('💬 CommentableMarkdownContent: Effect cleanup');
      cancelAnimationFrame(raf);
      cleanupEventListeners();
      if (rebindTimeoutRef.current) {
        clearTimeout(rebindTimeoutRef.current);
        rebindTimeoutRef.current = null;
      }
    };
  }, [processedContent, handleElementClick, cleanupEventListeners, selectedCommentId, showCommentDialog, addCommentableFeatures]);

  const handleCommentSubmit = useCallback((commentContent: string) => {
    // console.log('💬 CommentableMarkdownContent: Submitting comment for blockId:', selectedBlockId);
    if (selectedBlockId) {
      onAddComment({
        blockId: selectedBlockId,
        content: commentContent
      });
      
      // Synchronously clear guard refs to prevent race conditions
      selectedBlockIdRef.current = null;
      showCommentDialogRef.current = false;
      
      // Reset state after successful submission
      setShowCommentDialog(false);
      setSelectedBlockId(null);
      setSelectedText('');
      setSelectionState('idle');
      
      // console.log('💬 CommentableMarkdownContent: Comment submitted successfully');
    }
  }, [selectedBlockId, onAddComment]);

  const handleCloseDialog = useCallback(() => {
    // console.log('💬 CommentableMarkdownContent: Closing dialog (cancelled)');
    
    // Synchronously clear guard refs to prevent race conditions
    selectedBlockIdRef.current = null;
    showCommentDialogRef.current = false;
    
    // Reset all state to ensure clean slate for next interaction
    setShowCommentDialog(false);
    setSelectedBlockId(null);
    setSelectedText('');
    setSelectionState('idle');
    
    // console.log('💬 CommentableMarkdownContent: Dialog closed, ready for next interaction');
  }, []);

  // Reset state when comments change (indicating panel interaction)
  useEffect(() => {
    // If the dialog is not open but we're not in idle state, reset to idle
    if (!showCommentDialog && selectionState !== 'idle') {
      // console.log('💬 CommentableMarkdownContent: Resetting state to idle after external interaction');
      setSelectionState('idle');
      setSelectedBlockId(null);
      setSelectedText('');
    }
  }, [comments, showCommentDialog, selectionState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // console.log('💬 CommentableMarkdownContent: Component unmounting');
      cleanupEventListeners();
    };
  }, [cleanupEventListeners]);

  return (
    <div style={{ 
      lineHeight: '1.6',
      userSelect: showCommentDialog ? 'none' : 'text', // Only block text selection when modal is open
      WebkitUserSelect: showCommentDialog ? 'none' : 'text',
      MozUserSelect: showCommentDialog ? 'none' : 'text',
      msUserSelect: showCommentDialog ? 'none' : 'text'
      // Removed pointerEvents blocking - this was causing the modal interaction issue
    }}>
      <div ref={contentRef}>
        <MarkdownRenderer content={content} />
      </div>

      <CommentDialog
        isOpen={showCommentDialog}
        onClose={handleCloseDialog}
        onSubmit={handleCommentSubmit}
        blockContent={selectedText}
      />
      
      {selectionState === 'selecting' && !showCommentDialog && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '4px',
          zIndex: 1000,
          fontSize: '0.9rem'
        }}>
          Opening comment dialog...
        </div>
      )}
    </div>
  );
};

export default CommentableMarkdownContent;
